import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { timeToUtc, TypedFormControl, TypedFormGroup } from 'core';

export class CustomValidators {
    public static VALIDATOR_MASK: Array<string | RegExp> = [/\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/, '-', /\d/];

    public static get email(): ValidatorFn {
        /* eslint-disable-next-line max-len */
        const regex = /^((([a-zA-Z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+(\.([a-zA-Z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+)*)|((\x22)((((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(([\x01-\x08\x0b\x0c\x0e-\x1f\x7f]|\x21|[\x23-\x5b]|[\x5d-\x7e]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(\\([\x01-\x09\x0b\x0c\x0d-\x7f]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]))))*(((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(\x22)))@((([a-zA-Z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-zA-Z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-zA-Z]|\d|-||_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-zA-Z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-zA-Z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+|(([a-zA-Z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+([a-zA-Z]+|\d|-|\.{0,1}|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])?([a-zA-Z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))$/;
        const asciiRegex = /^[\x00-\xFF]+$/;
        const whiteSpaceRegex = /\s/;
        return (control: AbstractControl) => {
            if (!!control.value && (control.value.length <= 5 || whiteSpaceRegex.test(control.value) || !regex.test(control.value) || !asciiRegex.test(control.value))) {
                return { email: true };
            }
            return null;
        };
    }

    public static maxValue(max: number): ValidatorFn {
        return (control: AbstractControl): { maxValue: any } => {
            const input = control.value;
            if (!isNaN(input) && input > max) {
                return { maxValue: { maxValue: max } };
            } else {
                return null;
            }
        };
    }
    public static minValue(min: number): ValidatorFn {
        return (control: AbstractControl): { minValue: any } => {
            const input = control.value;
            if (!isNaN(input) && input < min) {
                return { minValue: { minValue: min } };
            } else {
                return null;
            }
        };
    }
    public static exactLength: (requiredLength: number) => ValidatorFn = (targetLength: number) => (control: AbstractControl): any => {
        if (!!control.value && String(control.value).length !== targetLength) {
            return { exactLength: { requiredLength: targetLength } };
        }
        return null;
    };
    public static onlyDigitsAndCyrillic: () => ValidatorFn = () => (control: AbstractControl) => {
        const regexp = /[^а-яА-ЯёЁ0-9]+/g;
        if (!!control.value && regexp.test(control.value)) {
            return { onlyDigitsAndCyrillic: true };
        }
        return null;
    };
    public static onlyDigitsAndCyrillicForEachElement: () => ValidatorFn = () => (control: AbstractControl): any => {
        const regexp = /[^а-яА-ЯёЁ0-9]+/g;
        if (!control.value) {
            return null;
        }
        if ((control.value as string[]).find(el => regexp.test(el))) {
            return { onlyDigitsAndCyrillicForEachElement: { message: 'onlyDigitsAndCyrillic' } };
        }
        return null;
    };
    public static onlyDigits: ValidatorFn = (control: AbstractControl) => {
        const regexp = /[^0-9]+/g;
        if (!!control.value && regexp.test(control.value)) {
            return { onlyDigits: true };
        }
        return null;
    };
    public static onlyCyrillicAndLatin: () => ValidatorFn = () => (control: AbstractControl) => {
        const regexp = /[^а-яА-ЯёЁa-zA-Z -]+/g;
        if (!!control.value && regexp.test(control.value)) {
            return { onlyCyrillicAndLatin: true };
        }
        return null;
    };
    public static onlyLatinAndMarks: () => ValidatorFn = () => (control: AbstractControl) => {
        const regexp = /[^a-zA-Z /\\\-']+/g;
        if (!!control.value && regexp.test(control.value)) {
            return { onlyCyrillicAndLatin: true };
        }
        return null;
    };
    public static namesValidator: () => ValidatorFn = () => (control: AbstractControl) => {
        const regexp = /^[-—A-Za-zА-Яа-яЁё'` ]+$/g ;
        if (!control.value) {
            return null
        }
        if (String(control.value).length < 2) {
            return { nameMinLength: { minLength: 2 } }
        }
        if (!regexp.test(control.value)) {
            return { onlyCyrillicAndLatin: true };
        }
        return null;
    };
    public static onlyDigitsAndLatin: () => ValidatorFn = () => (control: AbstractControl) => {
        const regexp = /[^0-9a-zA-Z]+/g;
        if (!!control.value && regexp.test(control.value)) {
            return { onlyDigitsAndLatin: true };
        }
        return null;
    }
    public static onlyDigitsAndLeters: () => ValidatorFn = () => (control: AbstractControl) => {
        const regexp = /[^0-9a-zA-ZА-Яа-яЁё]+/g;
        if (!!control.value && regexp.test(control.value)) {
            return { onlyDigitsAndLatin: true };
        }
        return null;
    }
    public static onlyDigitsAndLatinAndSymbols: () => ValidatorFn = () => (control: AbstractControl) => {
        const regexp = /[^a-zA-Z0-9-!$%^&*@#()_+|~=`{}\[\]:";'<>?,.\/№ ]+/g;
        if (!!control.value && regexp.test(control.value)) {
            return { onlyDigitsAndLatinAndSymbols: true };
        }
        return null;
    };
    public static onlyFourDigitsAndOneLatinLetter: () => ValidatorFn = () => (control: AbstractControl) => {
        const regexp = /[a-zA-Z][0-9]{4}|[0-9][a-zA-Z][0-9]{3}|[0-9]{2}[a-zA-Z][0-9]{2}|[0-9]{3}[a-zA-Z][0-9]|[0-9]{4}[a-zA-Z]|^$/g;
        if (!control.value || (regexp.test(control.value) && String(control.value).length === 5)) {
            return null;
        } else {
            return { onlyFourDigitsAndLatinLetter: true };
        }
    };
    public static onlyFromToDigits: (min: number, max: number) => ValidatorFn = (targetMin: number, targetMax: number) => (control: AbstractControl): any => {
        const regexp = /[^0-9]+/g;
        if (!!control.value && (regexp.test(control.value) || String(control.value).length < targetMin || String(control.value).length > targetMax)) {
            return { onlyFromToDigits: { min: targetMin, max: targetMax } };
        }
        return null;
    };
    public static atLeastOneChecked(): ValidatorFn {
        return (control: TypedFormGroup<any>): { required: any } => {
            const formGroupControls = control.controls;
            for (const key of Object.keys(formGroupControls)) {
                if (formGroupControls[key].value === true) {
                    return null;
                }
            }
            return { required: null };
        };
    }
    public static tinValidator(): ValidatorFn {
        return (control: AbstractControl) => {
            const regexp = /^\d{10}(\d\d)?$/g;
            if (!!control.value && !regexp.test(control.value)) {
                return { tin: true };
            }
            return null;
        };
    }
    public static kppOfLargestTaxpayerValidator(): ValidatorFn {
        return (control: AbstractControl) => {
            const regexp = /^99\d{7}$/g;
            if (!!control.value && !regexp.test(control.value)) {
                return { kppOfLargestTaxpayer: true };
            }
            return null;
        };
    }
    public static notEqual(invalidString: string): ValidatorFn {
        return (control: AbstractControl) => {
            const input = control.value;
            if (input === invalidString) {
                return { notEqual: true };
            } else {
                return null;
            }
        };
    }
    public static equal(validString: string, message: string = 'equal'): ValidatorFn {
        return (control: AbstractControl) => {
            const input = control.value;
            if (!input) {
                return null;
            }

            if (input !== validString) {
                return { [message]: { value: validString} };
            }

            return null;
        };
    }
    public static requiredEnum(control: AbstractControl): ValidationErrors | null {
        return control.value == null || control.value.length === 0 || control.value === 'None' ? { required: true } : null;
    }
    public static requiredTrue(message: string = 'required'): ValidatorFn {
        return (control: AbstractControl): any => {
            return control.value !== true ? { [message]: true } : null;
        };
    }
    public static exactLengthOfControlWithMask(requiredLength: number, maskReplacementSymbol: string = '_'): ValidatorFn {
        return (control: AbstractControl): any => {
            if (!!control.value && control.value.indexOf(maskReplacementSymbol) !== -1) {
                return { exactLengthDigits: { requiredLength } };
            }
            return null;
        };
    }
    public static attachmentRequired(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (!control.value?.length) {
                return { attachmentRequired: true };
            }
            return null;
        };
    }
    public static onlyLetters(controlName: string): ValidatorFn {
        return (control: TypedFormControl<string>): any => {
            const { value }: { value: string | null } = control;
            if (!control.parent || !value) {
                return null;
            }
            if (!value.match(/^[-—A-Za-zА-Яа-яЁё'` ]+$/)) {
                return { [`${controlName}Pattern`]: true };
            } else {
                return null;
            }
        };
    }
    public static patternWithMessage(pattern: RegExp, message: string): ValidatorFn {
    // Actually, message is firstly a message key in i18next namespace:validation:*key*
    // If there is no key only then it falls back to display this as a message
        return (ctrl: AbstractControl) => {
            if (!ctrl.value) {
                return null;
            }
            if (!pattern.test(ctrl.value)) {
                return { patternError: { message } };
            }
            return null;
        };
    }
    public static invalidValueValidator(invalidValue: any, message: string): ValidatorFn {
        return (ctrl: AbstractControl) => {
            if (!ctrl.value) {
                return null;
            }
            if (ctrl.value === invalidValue) {
                return { invalid: { message } };
            }
            return null
        };
    }
    // Используем функцию для получения массива значений для сравнения для случая:
    // 1. Значения содержат не только значения из формы
    // 2. Значения могут меняться после добавления валидатора к контролу
    public static uniqueInArrayValidator(getArrayFn: Function, message: string = 'uniqueInArrayError'): ValidatorFn {
        return (ctrl: AbstractControl) => {
            if (!ctrl.value) {
                return null
            }
            const compareArray = getArrayFn()
            const isValueUnique = compareArray.filter((value: any) => value === ctrl.value).length === 1
            if (!isValueUnique) {
                return { [message]: { message } }
            }
            return null
        }
    }
    // Валидатор для проверки, что даты задают валидный диапазон
    public static validFromValidator(validToControl: TypedFormControl<string>, message: string = 'validFromMustBeEarlierThanValidTo'): ValidatorFn {
        return (validFromControl: TypedFormControl<string>) => {
            if (!validFromControl || !validFromControl.value || !validToControl || !validToControl.value) {
                return null;
            }
            const validFromString = timeToUtc(validFromControl.value);
            const validFromValue = new Date(validFromString);

            const validToString = timeToUtc(validToControl.value)
            const validToValue = new Date(validToString);

            if (validFromValue.getTime() >= validToValue.getTime()) {
                return { [message]: { message } }
            }
            return null;
        };
    }
    public static requiredWithUniqueMessage(message: string): ValidatorFn {
        return (ctrl: AbstractControl) => {
            const value = ctrl.value;
            return value == null || ((typeof value === 'string' || Array.isArray(value)) && value.length === 0)
                ? { [message]: message }
                : null
        }
    }
    public static poaNumberFormat(): ValidatorFn {
        return (control: AbstractControl) => {
            const regexp = new RegExp('^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$');
            if (!!control.value && !regexp.test(control.value)) {
                return { poaNumberFormat: true };
            }
            return null;
        }
    }
}
